import { useQuery } from "@tanstack/react-query"
import api from "api"
import ErrorPage from "components/ErrorPage"
import FullPageLoading from "components/FullPageLoading"
import PageContainer from "components/PageContainer"
import React from "react"
import { useParams } from "react-router-dom"
import useHideGlobalBottomNavigation from "utils/hooks/useGlobalHideBottomNavigation"
import useHideTopNavigation from "utils/hooks/useHideTopNavigation"

import AboutThisReport from "./_components/AboutThisReport"
import DetectionSummary from "./_components/DetectionSummary"
import FaceDetectionIrregularities from "./_components/FaceDetectionIrregularities"
import GradientHeader from "./_components/GradientHeader"
import OnThisPage from "./_components/OnThisPage"
import ViolationSummaryCard from "./_components/ViolationsSummaryCard"

const CpatProctoringReport = () => {
  useHideTopNavigation()
  useHideGlobalBottomNavigation()

  const { attemptId } = useParams()

  const {
    data: cpatAssessmentViolations,
    isLoading,
    isError,
  } = useQuery({
    queryFn: () =>
      api.cpat.retrieveProctoringResults({
        urlParams: { attemptId: attemptId ?? "" },
      }),
    queryKey: ["cpat_assessment_violations", attemptId],
    enabled: !!attemptId,
  })

  if (isError) {
    return <ErrorPage errorStatus={500} />
  }

  if (isLoading || !attemptId || !cpatAssessmentViolations) {
    return <FullPageLoading />
  }

  const hasFaceIrregularities =
    cpatAssessmentViolations.faceStats.noFace > 0 ||
    cpatAssessmentViolations.faceStats.multipleFaces > 0

  return (
    <div className="relative bg-surface-50 pb-[240px]">
      <GradientHeader dateCreated={cpatAssessmentViolations.dateCreated} />
      <PageContainer className="relative z-10 md:-mt-3">
        <div className="flex grid-cols-12 flex-col gap-2 md:grid md:gap-3">
          <div className="top-6 col-span-3 flex w-full flex-col gap-3 self-start md:sticky">
            <ViolationSummaryCard
              totalViolations={
                cpatAssessmentViolations.violations.totalViolations +
                cpatAssessmentViolations.faceStats.noFace +
                cpatAssessmentViolations.faceStats.multipleFaces
              }
            />
            <OnThisPage
              items={[
                { id: "detection-summary", label: "Detection Summary" },
                {
                  id: "face-detection-irregularities",
                  label: "Face Detection Irregularities",
                },
                { id: "about-report", label: "About this report" },
              ]}
            />
          </div>
          <div className="col-span-9 flex flex-col gap-2 md:gap-3">
            <DetectionSummary
              id="detection-summary"
              className="scroll-mt-6"
              data={cpatAssessmentViolations}
            />
            {hasFaceIrregularities && (
              <FaceDetectionIrregularities
                id="face-detection-irregularities"
                className="scroll-mt-6"
                noFaceImages={cpatAssessmentViolations.noFaceImages}
                multipleFaceImages={cpatAssessmentViolations.multipleFaceImages}
              />
            )}
            <AboutThisReport id="about-report" className="scroll-mt-6" />
          </div>
        </div>
      </PageContainer>
    </div>
  )
}

export default CpatProctoringReport
